import { deleteObject, normalizeStorageError } from '@Utils/S3Storage'
import toastr from 'toastr'

import 'toastr/build/toastr.css'

const TOASTR_OPTIONS = {
  closeButton: true,
  timeOut: 0,
  extendedTimeOut: 0,
}

type ExtendedWindow = typeof window & {
  EditorSources?: { readonly e2eMode?: boolean; readonly s3Bucket: string }
  Checksub?: { readonly e2eMode?: boolean; readonly s3Bucket: string }
  Rollbar?: { error: (error: Error, context?: unknown) => void }
}

const extendedWindow = window as ExtendedWindow

export interface IS3RemovalOptions {
  readonly fileUrl: string
  readonly onSuccess?: () => void
  readonly onError?: (error: Error) => void
}

class S3Remover {
  constructor(private readonly options: IS3RemovalOptions) {}

  async call(): Promise<void> {
    const { bucket, key } = this.extractS3Location(this.options.fileUrl)

    try {
      await deleteObject(bucket, key)
      this.options.onSuccess?.()
    } catch (error: unknown) {
      const storageError = normalizeStorageError(error)

      if (extendedWindow.Rollbar) {
        extendedWindow.Rollbar.error(storageError, { bucket, key })
      }

      if (this.options.onError) {
        this.options.onError(storageError)
      } else {
        toastr.error('The file could not be deleted. Please try again later.', 'Delete failed', TOASTR_OPTIONS)
      }

      throw storageError
    }
  }

  private extractS3Location(fileUrl: string): { bucket: string; key: string } {
    let url: URL

    try {
      url = new URL(decodeURI(fileUrl))
    } catch (error) {
      throw new Error('Invalid file URL')
    }

    const pathname = url.pathname.replace(/^\/+/, '')

    if (!pathname) {
      throw new Error('Invalid file URL')
    }

    if (
      (extendedWindow.EditorSources?.e2eMode || extendedWindow.Checksub?.e2eMode) &&
      pathname.startsWith('e2e/storage/')
    ) {
      const [, , bucket, ...keyParts] = pathname.split('/')

      if (!bucket || keyParts.length === 0) {
        throw new Error('Cannot resolve E2E bucket/key from file URL')
      }

      return { bucket: decodeURIComponent(bucket), key: keyParts.map(decodeURIComponent).join('/') }
    }

    const hostParts = url.hostname.split('.')

    if (hostParts[0] === 's3') {
      const [bucket, ...keyParts] = pathname.split('/')

      if (!bucket || keyParts.length === 0) {
        throw new Error('Cannot resolve bucket/key from file URL')
      }

      return { bucket, key: keyParts.join('/') }
    }

    const s3Index = hostParts.findIndex(part => part === 's3')

    if (s3Index > 0) {
      const bucket = hostParts.slice(0, s3Index).join('.')
      return { bucket, key: pathname }
    }

    const bucketName = extendedWindow.EditorSources?.s3Bucket ?? extendedWindow.Checksub?.s3Bucket

    if (!bucketName) {
      throw new Error('Cannot resolve S3 bucket for file deletion')
    }

    return { bucket: bucketName, key: pathname }
  }
}

export default S3Remover
